// Locale-aware accessors for the /[lang]/events, /[lang]/tours, /[lang]/boats
// and /[lang]/articles hubs' hand-written SEO copy. Same approach as wine-content.ts: read off the
// imported resource JSON directly, since `intro` / `sections` / `faq` are
// arrays (no `returnObjects` dance through t()).
//
// The copy is baked into static HTML and lives for months, while the catalogues
// change daily — so it states only what doesn't drift (annual festivals,
// landmark venues and destinations, how booking works): no counts, prices or
// "most listings in X".

import type { Locale } from '@todo/core/i18n/locales';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

export type HubText = {
  /** Lead paragraph(s) under the <h1>, above the cards. */
  intro: string[];
  /** <h2> sections below the cards. */
  sections: { heading: string; paragraphs: string[] }[];
  /** <h2> over the per-city landing-page links. */
  citiesHeading: string;
};

export type HubCopy = HubText & {
  faqHeading: string;
  faq: { q: string; a: string }[];
};

type Hub = 'events' | 'tours';

/** The /articles hub: lead paragraph(s) + the heading over the city-guide links. */
export type ArticlesHubText = { intro: string[]; cityGuidesHeading: string };

type HubResource = {
  eventsHub: HubCopy;
  toursHub: HubCopy;
  boatsHub: HubText;
  articlesHub: ArticlesHubText;
};

const resources: Record<Locale, HubResource> = { en, fr, ru };

export function hubCopy(locale: Locale, hub: Hub): HubCopy {
  return hub === 'events' ? resources[locale].eventsHub : resources[locale].toursHub;
}

/** The /boats hub's text. Its FAQ is the older `boats.faq` — see boatFaq(). */
export function boatsHubText(locale: Locale): HubText {
  return resources[locale].boatsHub;
}

/**
 * The /articles hub's text. Says nothing about individual articles — the set
 * changes in Sanity without a code change.
 */
export function articlesHubText(locale: Locale): ArticlesHubText {
  return resources[locale].articlesHub;
}
